import { useState, useEffect, useRef } from 'react';
import { MarketIndex } from '@/services/market-data-service';
import { getMarketIndicesWithFallback } from '@/services/alpaca-market-indices';

interface UseMarketIndicesOptions {
  refreshInterval?: number; // Refresh interval in milliseconds
  initialIndices?: string[]; // Initial indices to fetch
}

interface MarketIndicesResult {
  indices: MarketIndex[];
  loading: boolean;
  error: string | null;
  lastUpdated: Date | null;
  refreshData: () => Promise<void>;
}

export function useMarketIndices(options: UseMarketIndicesOptions = {}): MarketIndicesResult {
  const [indices, setIndices] = useState<MarketIndex[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  
  // Interval reference to clean up on unmount or visibility change
  const intervalRef = useRef<number | null>(null);
  // Flag to track visibility state
  const isVisibleRef = useRef<boolean>(true);
  
  const refreshInterval = options.refreshInterval || 5 * 60 * 1000; // Default to 5 minutes
  
  const fetchIndicesData = async () => {
    // Only fetch data if the page is visible
    if (!isVisibleRef.current) return;
    
    setLoading(true);
    setError(null);
    
    try {
      // Use Alpaca service with fallback
      const data = await getMarketIndicesWithFallback();
      setIndices(data);
      setLastUpdated(new Date());
    } catch (err: any) {
      console.error('Error fetching market indices:', err);
      setError('Failed to load market data');
    } finally {
      setLoading(false);
    }
  };
  
  const startAutoRefresh = () => {
    // Clear any existing interval
    if (intervalRef.current !== null) {
      clearInterval(intervalRef.current);
    }
    
    // Only set up auto-refresh if the page is visible
    if (isVisibleRef.current) {
      intervalRef.current = window.setInterval(fetchIndicesData, refreshInterval);
    }
  };
  
  const stopAutoRefresh = () => {
    if (intervalRef.current !== null) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };
  
  // Handle visibility change
  useEffect(() => {
    const handleVisibilityChange = () => {
      isVisibleRef.current = document.visibilityState === 'visible';
      
      // If page becomes visible, fetch data immediately and start auto-refresh
      if (isVisibleRef.current) {
        fetchIndicesData();
        startAutoRefresh();
      } else {
        // If page becomes hidden, stop auto-refresh
        stopAutoRefresh();
      }
    };
    
    // Set up visibility change listener
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    // Initial fetch and auto-refresh setup
    fetchIndicesData();
    startAutoRefresh();
    
    // Cleanup
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      stopAutoRefresh();
    };
  }, [refreshInterval]);
  
  return {
    indices,
    loading,
    error,
    lastUpdated,
    refreshData: fetchIndicesData
  };
}